import { BarChart3, Download, MousePointerClick } from 'lucide-react'
import { useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import OpenClawLandingPage from '../components/openclaw/OpenClawLandingPage'
import AdTencentDownloadButtons from '../components/openclaw/AdTencentDownloadButtons'
import api from '../utils/api'

// 从 uxinfo/adUxInfo 管道字符串解析 creative_id（第7段，0-based 索引7）
function parseCreativeIdFromUxInfo(uxinfoStr) {
  if (!uxinfoStr) return ''
  const parts = String(uxinfoStr).split('|')
  return parts.length > 7 ? parts[7] : ''
}

function OpenClawAdTencentDisplay() {
  const [searchParams] = useSearchParams()
  const tencentId = String(searchParams.get('tencentId') || searchParams.get('tencentid') || '').trim()
  const callbackUrl = String(searchParams.get('cb') || '').trim()
  // 腾讯广告 click_id：非微信流量为 qz_gdt，微信流量为 gdt_vid，点击监测链接为 tencentid
  const clickId = String(searchParams.get('qz_gdt') || searchParams.get('gdt_vid') || searchParams.get('clickId') || searchParams.get('click_id') || searchParams.get('tencentid') || '').trim()
  // 广告参数：ad_id 优先取 ad_id，其次取微信广告 wx_aid；creative_id 优先取 creative_id，其次从 uxinfo/adUxInfo 解析
  const adId = String(searchParams.get('ad_id') || searchParams.get('adId') || searchParams.get('wx_aid') || '').trim()
  const creativeId = String(searchParams.get('creative_id') || searchParams.get('creativeId') || parseCreativeIdFromUxInfo(searchParams.get('adUxInfo')) || parseCreativeIdFromUxInfo(searchParams.get('uxinfo')) || '').trim()
  // 从 URL 路径中提取页码（如 /adtencent-101 → 101）
  const pageMatch = window.location.pathname.match(/\/adtencent-(\d+)/)
  const pageNo = pageMatch ? parseInt(pageMatch[1], 10) : 0
  const pagePath = pageNo > 0 ? `adtencent-${pageNo}` : ''

  useEffect(() => {
    let cancelled = false

    const trackVisit = async () => {
      if ((!callbackUrl && !clickId) || cancelled) return
      try {
        await api.post('/adtencent/visit', {
          tencentId,
          callback_url: callbackUrl || undefined,
          click_id: clickId || undefined,
          page_url: window.location.href,
          page_path: pagePath || undefined,
          ad_id: adId || undefined,
          creative_id: creativeId || undefined
        })
      } catch (error) {
        console.error('记录腾讯落地页访问失败:', error)
      }
    }

    trackVisit()
    return () => {
      cancelled = true
    }
  }, [tencentId, callbackUrl, clickId])

  return (
    <OpenClawLandingPage
      theme="rose"
      productName="DClaw"
      showFriendLinks={false}
      seo={{
        title: 'DClaw 下载',
        description: 'DClaw 下载页，提供适用于不同系统版本的安装包下载入口。',
        keywords: 'DClaw下载, DClaw安装包, 官方下载',
        disablePromoSuffix: true
      }}
      hero={{
        eyebrow: 'DClaw 下载',
        title: 'DClaw 下载',
        subtitle: '下载并开始使用 DClaw',
        description:
          '本页面提供 DClaw 不同系统版本的安装包下载入口，页面结构延续官网现有下载页风格。\n下载安装完成后，可按客户端正常流程启动并登录使用。',
        tags: ['官方下载', '安装包下载', '多系统版本', '登录使用'],
        guideTitle: '下载、安装、登录即可开始使用',
        guideDescription: '按页面步骤选择系统版本下载安装包，完成安装后即可启动客户端并登录。',
        guideBullets: [
          '可直接选择对应系统版本下载安装包。',
          '根据你的设备系统选择对应版本下载安装包。',
          '安装完成后打开客户端并正常登录使用。'
        ]
      }}
      screenshotCaption="该页面延续现有官方下载体验，帮助用户快速完成版本选择和安装。"
      
      highlights={[
        {
          icon: MousePointerClick,
          title: '页面风格一致',
          description: '整体结构延续现有官方下载页风格，减少新页面认知成本。'
        },
        {
          icon: Download,
          title: '多系统版本下载',
          description: '支持 Windows、Mac Intel 和 Mac Apple 版本下载，方便用户快速选择适合自己设备的安装包。'
        },
        {
          icon: BarChart3,
          title: '安装后即可使用',
          description: '下载安装完成后，用户可以直接进入客户端并按正常流程登录使用。'
        }
      ]}
      steps={[
        {
          title: '进入下载页面',
          description: '用户进入页面后即可查看适合当前设备的安装包版本。'
        },
        {
          title: '选择系统版本下载',
          description: '根据设备系统点击对应下载按钮，浏览器开始下载目标安装包。'
        },
        {
          title: '完成安装',
          description: '下载完成后按系统提示安装客户端。'
        },
        {
          title: '启动并登录使用',
          description: '安装完成后打开客户端并登录，即可正常开始使用 DClaw。'
        }
      ]}
      sections={[
        {
          title: '下载安装流程简单直接',
          description:
            '本页面按照常见官方下载页的方式组织内容，用户只需要选择正确系统版本下载安装，再完成登录即可正常使用。',
          items: [
            'Windows、Mac Intel、Mac Apple 版本入口清晰。',
            '下载、安装、登录三个步骤衔接顺畅。',
            '重复下载或重新安装不会影响正常使用。',
            '页面信息简洁，便于快速完成版本选择。'
          ]
        },
        {
          title: '下载信息清晰易懂',
          description: '页面只保留用户下载、安装和登录所需的信息，阅读路径更直接，使用起来更顺手。',
          items: [
            '页面风格统一，下载安装入口更直观。',
            '不同系统版本的下载按钮一目了然。',
            '安装完成后可直接进入客户端登录。',
            '适合首次下载和重复下载安装使用。'
          ]
        }
      ]}
      closing={{
        title: '选择系统版本并开始下载',
        description: '请直接选择你的系统版本下载安装包，完成安装后即可登录并开始使用。',
        footerTitle: '下载、安装、登录一步完成',
        footerDescription: '选择适合你的系统版本下载安装包，完成安装后即可启动客户端并正常登录使用。',
        footerTags: ['DClaw下载', '多系统版本', '安装体验', '客户端下载']
      }}
      downloadContent={<AdTencentDownloadButtons theme="rose" tencentId={tencentId} callbackUrl={callbackUrl} clickId={clickId} pageNo={pageNo} />}
    />
  )
}

export default OpenClawAdTencentDisplay
